import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import CreateKanbanCardModal from "../../components/CreateKanbanCardModal";

// Regras de finalização que dependem de configuração da empresa e precisam de
// uma resposta do atendente ANTES do PUT que fecha o chamado:
//   crmConvertedQuestion = enabled            -> pergunta "Convertido?" (sim/não/n.a.)
//   kanbanCardCreationMode = mandatory|optional -> chamado sem card no Kanban:
//       obrigatório: só fecha depois de criar o card; opcional: criar ou pular.
// Viviam só no TicketActionButtonsCustom, então o menu de contexto da lista
// fechava o chamado sem passar por elas. Aqui valem para qualquer ponto de
// fechamento; quem faz o PUT continua sendo o chamador (useCloseTicketGuard).
//
// Uso:
//   const { prepareClose, dialogs } = useTicketCloseRules();
//   const { proceed, converted } = await prepareClose(ticket);
//   if (proceed) await closeTicket(ticket.id, { ...payload, ...(converted ? { converted } : {}) });
//   ...e renderizar {dialogs} na árvore.
//
// Ação em massa: passe o mesmo objeto `batch` em todas as chamadas do lote (o
// mesmo do useCloseTicketGuard). Cada pergunta aparece uma vez e a resposta vale
// para todos; cancelar marca `batch.cancelled`, e o chamador interrompe o lote.

// O menu de contexto fica montado a sessão inteira, então a configuração é
// relida a cada finalização (com um cache curto para a ação em massa não
// repetir a chamada a cada chamado).
const SETTINGS_CACHE_MS = 15000;

const DEFAULT_SETTINGS = {
  kanbanCardCreationMode: "optional",
  crmConvertedQuestion: "disabled",
};

const isRealTicket = (ticket) =>
  Boolean(ticket?.id) && !/^contact-/.test(String(ticket.id));

const ticketCaption = (ticket) => {
  if (!ticket) return "";
  const name = ticket.contact?.name;
  return name ? `#${ticket.id} · ${name}` : `#${ticket.id}`;
};

const useTicketCloseRules = () => {
  // { ticket } enquanto o diálogo/modal correspondente está aberto
  const [convertedDialog, setConvertedDialog] = useState(null);
  const [kanbanDialog, setKanbanDialog] = useState(null);
  const [kanbanModal, setKanbanModal] = useState(null);

  // resolve da Promise que o prepareClose está aguardando em cada etapa
  const convertedResolveRef = useRef(null);
  const kanbanDialogResolveRef = useRef(null);
  const kanbanModalResolveRef = useRef(null);

  const settingsRef = useRef({ loadedAt: 0, values: DEFAULT_SETTINGS });

  // Fechar o chamado pode desmontar a árvore que renderiza os diálogos (o chat
  // sai da tela, o popup fecha) antes de o modal do card terminar seu onClose
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Saiu da tela com uma pergunta aberta (ex.: a lista desmontada no meio de
      // uma ação em massa): conta como cancelada, senão o prepareClose fica
      // esperando para sempre
      [
        [convertedResolveRef, null],
        [kanbanDialogResolveRef, "cancel"],
        [kanbanModalResolveRef, false],
      ].forEach(([ref, answer]) => {
        const resolve = ref.current;
        ref.current = null;
        if (resolve) resolve(answer);
      });
    };
  }, []);

  const loadSettings = async () => {
    const now = Date.now();
    if (now - settingsRef.current.loadedAt < SETTINGS_CACHE_MS) {
      return settingsRef.current.values;
    }

    try {
      const { data } = await api.get("/settings");
      const valueOf = (key) => data.find((s) => s.key === key)?.value;
      settingsRef.current = {
        loadedAt: now,
        values: {
          kanbanCardCreationMode:
            valueOf("kanbanCardCreationMode") || DEFAULT_SETTINGS.kanbanCardCreationMode,
          crmConvertedQuestion:
            valueOf("crmConvertedQuestion") || DEFAULT_SETTINGS.crmConvertedQuestion,
        },
      };
    } catch (err) {
      // Sem a configuração segue com a última conhecida (ou os padrões)
      console.error("Erro ao carregar configurações de finalização", err);
    }

    return settingsRef.current.values;
  };

  const hasKanbanCard = async (ticketId) => {
    try {
      const { data } = await api.get(`/kanban/cards/ticket/${ticketId}`);
      return Array.isArray(data) && data.length > 0;
    } catch (err) {
      console.error("Erro ao verificar card do Kanban", err);
      return false;
    }
  };

  // --- Etapa 1: "Convertido?" -> "sim" | "nao" | "nao_aplicavel" | null (cancelou)
  // Fora da tela não há onde perguntar: cada etapa conta como cancelada
  const askConverted = (ticket, isBatch) => {
    if (!isMountedRef.current) return Promise.resolve(null);
    return new Promise((resolve) => {
      convertedResolveRef.current = resolve;
      setConvertedDialog({ ticket, isBatch });
    });
  };

  const finishConverted = (value) => {
    const resolve = convertedResolveRef.current;
    convertedResolveRef.current = null;
    if (isMountedRef.current) setConvertedDialog(null);
    if (resolve) resolve(value);
  };

  // --- Etapa 2: chamado sem card -> "create" | "skip" | "cancel"
  const askKanban = (ticket, mode, isBatch) => {
    if (!isMountedRef.current) return Promise.resolve("cancel");
    return new Promise((resolve) => {
      kanbanDialogResolveRef.current = resolve;
      setKanbanDialog({ ticket, mode, isBatch });
    });
  };

  const finishKanbanDialog = (answer) => {
    const resolve = kanbanDialogResolveRef.current;
    kanbanDialogResolveRef.current = null;
    if (isMountedRef.current) setKanbanDialog(null);
    if (resolve) resolve(answer);
  };

  // --- Etapa 3: modal de criação do card -> true (criou) | false (fechou sem criar)
  const openKanbanModal = (ticket) => {
    if (!isMountedRef.current) return Promise.resolve(false);
    return new Promise((resolve) => {
      kanbanModalResolveRef.current = resolve;
      setKanbanModal({ ticket });
    });
  };

  // Fecha o modal ANTES de devolver o controle: o fechamento do chamado pode
  // desmontar esta árvore, e o modal não pode ficar por cima do diálogo do
  // guarda de finalização. O onClose que o modal chama depois de criar o card
  // encontra o ref já limpo e não resolve nada de novo.
  const finishKanbanModal = (created) => {
    const resolve = kanbanModalResolveRef.current;
    kanbanModalResolveRef.current = null;
    if (isMountedRef.current) setKanbanModal(null);
    if (resolve) resolve(created);
  };

  // Devolve { proceed, converted }: proceed=false quando o atendente cancelou
  // em alguma etapa (ou fechou o modal do card sem criar).
  const prepareClose = useCallback(async (ticket, batch) => {
    // Pseudo-chamado da aba de Contatos ("contact-<id>") não tem linha em
    // Tickets: nem conversão nem card fazem sentido
    if (!isRealTicket(ticket)) {
      return { proceed: true };
    }

    const settings = await loadSettings();
    const isBatch = Boolean(batch);
    const cancel = () => {
      if (batch) batch.cancelled = true;
      return { proceed: false };
    };

    let converted;
    if (settings.crmConvertedQuestion === "enabled") {
      converted = batch?.converted || (await askConverted(ticket, isBatch));
      if (!converted) return cancel();
      if (batch) batch.converted = converted;
    }

    const mode = settings.kanbanCardCreationMode;
    // "Pular" no lote dispensa até a consulta do card nos chamados seguintes
    if ((mode === "mandatory" || mode === "optional") && batch?.kanbanAnswer !== "skip") {
      const exists = await hasKanbanCard(ticket.id);
      if (!exists) {
        // "Criar" no lote abre o cadastro do card de cada chamado sem card,
        // sem perguntar de novo
        const answer = batch?.kanbanAnswer || (await askKanban(ticket, mode, isBatch));
        if (answer === "cancel") return cancel();
        if (batch) batch.kanbanAnswer = answer;
        if (answer === "create") {
          const created = await openKanbanModal(ticket);
          if (!created) return cancel();
        }
      }
    }

    return { proceed: true, converted };
  }, []);

  const kanbanMode = kanbanDialog?.mode;

  const dialogs = (
    <>
      <Dialog
        open={Boolean(convertedDialog)}
        onClose={() => finishConverted(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{i18n.t("dashboard.conversions.dialog.title")}</DialogTitle>
        <DialogContent>
          {i18n.t("dashboard.conversions.dialog.message")}
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            {ticketCaption(convertedDialog?.ticket)}
          </Typography>
          {convertedDialog?.isBatch && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {i18n.t("ticketActionButtons.completionMessageBatchHint")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => finishConverted(null)} color="default">
            {i18n.t("dashboard.conversions.dialog.cancel")}
          </Button>
          <Button onClick={() => finishConverted("nao_aplicavel")} color="default">
            {i18n.t("dashboard.conversions.dialog.notApplicable")}
          </Button>
          <Button onClick={() => finishConverted("nao")} color="secondary">
            {i18n.t("dashboard.conversions.dialog.no")}
          </Button>
          <Button
            onClick={() => finishConverted("sim")}
            color="primary"
            variant="contained"
          >
            {i18n.t("dashboard.conversions.dialog.yes")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(kanbanDialog)}
        onClose={() => finishKanbanDialog("cancel")}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {kanbanMode === "mandatory"
            ? i18n.t("ticketActionButtons.mandatoryKanbanCard")
            : i18n.t("ticketActionButtons.optionalKanbanCard")}
        </DialogTitle>
        <DialogContent>
          {kanbanMode === "mandatory"
            ? i18n.t("ticketActionButtons.mandatoryKanbanCardMessage")
            : i18n.t("ticketActionButtons.optionalKanbanCardMessage")}
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            {ticketCaption(kanbanDialog?.ticket)}
          </Typography>
          {kanbanDialog?.isBatch && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {i18n.t("ticketActionButtons.completionMessageBatchHint")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {kanbanMode === "optional" && (
            <Button onClick={() => finishKanbanDialog("skip")} color="default">
              {i18n.t("ticketActionButtons.skip")}
            </Button>
          )}
          <Button
            onClick={() => finishKanbanDialog("create")}
            color="primary"
            variant="contained"
          >
            {i18n.t("ticketActionButtons.createCard")}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateKanbanCardModal
        open={Boolean(kanbanModal)}
        ticket={kanbanModal?.ticket}
        onCardCreated={() => finishKanbanModal(true)}
        onClose={() => finishKanbanModal(false)}
      />
    </>
  );

  return { prepareClose, dialogs };
};

export default useTicketCloseRules;
